import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from '@azure/msal-browser'
import { API_URL } from './api'
import { isHealRoute } from './heal/route'

type AuthConfig = {
  enabled: boolean
  test_bypass?: boolean
  dev_bypass?: boolean
  configured: boolean
  tenant_id: string | null
  client_id: string | null
  api_scope: string | null
  authority: string | null
}

const browserFetch = globalThis.fetch.bind(globalThis)
let client: PublicClientApplication | null = null
let account: AccountInfo | null = null
let config: AuthConfig | null = null
// MSAL allows only one interactive request (redirect/popup) at a time and
// throws BrowserAuthError('interaction_in_progress') for a second concurrent
// call. A session that has fully expired makes acquireTokenSilent fail on
// every in-flight request at once, so without this guard each one raced to
// start its own redirect -- the second call's thrown error propagated out of
// fetch() mid-render and tripped the app's ErrorBoundary ("Portal page could
// not load") instead of the page just following the first redirect to sign
// back in, which is what re-login "fixed" by hand.
let redirectStarted = false
const RETURN_PATH_KEY = 'incident-kb-auth-return-path'

function rememberPrivateRoute() {
  if (isHealRoute(window.location.pathname)) {
    window.sessionStorage.setItem(RETURN_PATH_KEY, '/heal')
  }
}

function isPortalApi(input: RequestInfo | URL): boolean {
  const value = input instanceof Request ? input.url : input.toString()
  const url = new URL(value, window.location.origin)
  const apiOrigin = new URL(API_URL, window.location.origin).origin
  return url.origin === apiOrigin
    && url.pathname.startsWith('/api/')
    && url.pathname !== '/api/auth/config'
}

async function accessToken(forceRefresh = false): Promise<string> {
  if (!client || !account || !config?.api_scope) return ''
  try {
    const response = await client.acquireTokenSilent({
      account,
      scopes: [config.api_scope],
      forceRefresh,
    })
    return response.accessToken
  } catch (error) {
    if (!(error instanceof InteractionRequiredAuthError)) throw error
    // The session needs a fresh interactive sign-in. Only the first caller
    // actually starts the redirect; every other request that hits this at
    // the same moment just waits for the page to navigate away instead of
    // racing to start a second one MSAL would reject.
    if (!redirectStarted) {
      redirectStarted = true
      try {
        rememberPrivateRoute()
        await client.acquireTokenRedirect({
          account,
          scopes: [config.api_scope],
        })
      } catch (redirectError) {
        // The redirect itself never got underway (e.g. blocked navigation) --
        // release the guard so the next request can try again instead of
        // silently sending unauthenticated calls forever.
        redirectStarted = false
        throw redirectError
      }
    }
    return ''
  }
}

async function authenticatedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  if (!config?.enabled || !isPortalApi(input)) return browserFetch(input, init)

  const send = async (forceRefresh: boolean) => {
    const token = await accessToken(forceRefresh)
    const headers = new Headers(input instanceof Request ? input.headers : undefined)
    new Headers(init.headers).forEach((value, key) => headers.set(key, value))
    if (token) headers.set('Authorization', `Bearer ${token}`)
    return browserFetch(input, { ...init, headers })
  }

  let response = await send(false)
  if (response.status === 401) response = await send(true)
  return response
}

export async function initializeAuthentication(): Promise<void> {
  // Try to load from runtime config first (injected by nginx)
  const runtimeEntra = window.appConfig?.entra
  if (runtimeEntra?.tenantId && runtimeEntra?.clientId && runtimeEntra?.apiScope) {
    config = {
      enabled: true,
      test_bypass: false,
      dev_bypass: false,
      configured: true,
      tenant_id: runtimeEntra.tenantId,
      client_id: runtimeEntra.clientId,
      api_scope: runtimeEntra.apiScope,
      authority: `https://login.microsoftonline.com/${runtimeEntra.tenantId}`,
    }
  } else {
    // Fall back to API endpoint for backward compatibility
    const response = await browserFetch(`${API_URL}/auth/config`)
    if (!response.ok) throw new Error('Authentication configuration could not be loaded')
    const responseBody = await response.text()
    try {
      config = JSON.parse(responseBody) as AuthConfig
    } catch {
      throw new Error('Authentication configuration returned an invalid response. Check the portal API address and try again.')
    }
  }
  if (!config.enabled) {
    if (config.test_bypass || config.dev_bypass) return
    throw new Error('Microsoft Entra ID authentication is required but is not enabled')
  }
  if (!config.configured || !config.client_id || !config.authority || !config.api_scope) {
    throw new Error('Entra ID authentication is enabled but not fully configured')
  }

  client = new PublicClientApplication({
    auth: {
      clientId: config.client_id,
      authority: config.authority,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
  })
  await client.initialize()
  const redirect = await client.handleRedirectPromise()
  account = redirect?.account || client.getAllAccounts()[0] || null
  if (!account) {
    rememberPrivateRoute()
    await client.loginRedirect({ scopes: [config.api_scope] })
    return
  }
  client.setActiveAccount(account)
  globalThis.fetch = authenticatedFetch as typeof globalThis.fetch
  const returnPath = window.sessionStorage.getItem(RETURN_PATH_KEY)
  if (returnPath === '/heal' && !isHealRoute(window.location.pathname)) {
    window.sessionStorage.removeItem(RETURN_PATH_KEY)
    window.location.replace('/heal')
  }
}

export async function signOut(): Promise<void> {
  if (!client || !account) return
  await client.logoutRedirect({ account })
}

export function authenticationEnabled(): boolean {
  return Boolean(config?.enabled)
}
